import { formatParisDateTime, type CarrierCoverageRow } from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { ErrorState } from '@/components/ui/error-state.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { useStoresList } from '@/features/stores/use-stores-list.js';
import { ApiError } from '@/lib/api/client.js';
import { cn } from '@/lib/cn.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchCarrierCoverage } from './api.js';

/**
 * Matrice de couverture (`02` §9) : chaque **mode de livraison × pays** des
 * profils d'expédition Shopify d'une boutique, rejoué contre la table de
 * règles. Une cellule marquée ⚠ retombe sur le transporteur par défaut —
 * c'est un trou de configuration, et c'est précisément ce que l'écran doit
 * rendre visible.
 *
 * Les profils sont lus chez Shopify : la requête n'est déclenchée qu'après
 * choix d'une boutique, et le bouton force un rafraîchissement.
 */
export interface CarrierCoverageMatrixProps {
  store: string | undefined;
  onStoreChange: (store: string) => void;
}

export function CarrierCoverageMatrix({
  store,
  onStoreChange,
}: CarrierCoverageMatrixProps): JSX.Element {
  const stores = useStoresList();
  const query = useQuery({
    queryKey: queryKeys.carriers.coverage(store ?? ''),
    queryFn: () => fetchCarrierCoverage(store ?? ''),
    enabled: store !== undefined && store !== '',
  });

  return (
    <PageSection
      title="Couverture des modes de livraison"
      description="Confronte les modes de livraison réellement configurés dans Shopify à la table de règles. ⚠ signale une combinaison qui part avec le transporteur par défaut."
      flush
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3">
        <Select value={store ?? ''} onValueChange={onStoreChange}>
          <SelectTrigger className="w-full sm:w-56" aria-label="Boutique">
            <SelectValue placeholder="Choisir une boutique…" />
          </SelectTrigger>
          <SelectContent>
            {(stores.data?.items ?? []).map((item) => (
              <SelectItem key={item.code} value={item.code}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          disabled={store === undefined || store === ''}
          isLoading={query.isFetching}
          onClick={() => {
            void query.refetch();
          }}
        >
          Récupérer les modes de livraison Shopify
        </Button>
        {query.data ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            Profils lus le {formatParisDateTime(new Date(query.data.fetchedAt))}
          </span>
        ) : null}
      </div>

      {store === undefined || store === '' ? (
        <EmptyState
          title="Choisissez une boutique"
          description="Les modes de livraison viennent des profils d’expédition Shopify de la boutique."
        />
      ) : (
        <CoverageBody
          isPending={query.isPending}
          isError={query.isError}
          error={query.error}
          rows={query.data?.rows ?? []}
          onRetry={() => {
            void query.refetch();
          }}
        />
      )}
    </PageSection>
  );
}

function CoverageBody({
  isPending,
  isError,
  error,
  rows,
  onRetry,
}: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  rows: CarrierCoverageRow[];
  onRetry: () => void;
}): JSX.Element {
  if (isPending) {
    return (
      <div className="flex flex-col gap-3 p-5">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={
          error instanceof ApiError
            ? error.message
            : 'Impossible de lire les profils d’expédition Shopify.'
        }
        detail={error instanceof ApiError ? error.detail : undefined}
        onRetry={onRetry}
        className="rounded-none border-0"
      />
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        title="Aucun mode de livraison"
        description="Cette boutique n’a aucun tarif d’expédition configuré dans Shopify, ou l’application n’a pas la permission « read_shipping »."
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Mode de livraison</TableHead>
          <TableHead>Zone</TableHead>
          <TableHead>Pays</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.profileId}-${row.zoneName}-${row.shippingMethod}`}>
            <TableCell className="min-w-48 align-top">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{row.shippingMethod}</span>
                {row.active ? null : <Badge tone="gray">Désactivé</Badge>}
              </div>
              <span className="block text-xs text-muted-foreground">{row.profileName}</span>
            </TableCell>
            <TableCell className="align-top">
              {row.zoneName}
              {row.restOfWorld ? (
                <span className="block text-xs text-muted-foreground">Reste du monde</span>
              ) : null}
            </TableCell>
            <TableCell className="min-w-64 align-top">
              <div className="flex flex-wrap gap-1.5">
                {row.cells.map((cell) => (
                  <span
                    key={cell.country}
                    className={cn(
                      'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md border px-1.5 text-xs',
                      cell.usedDefaultCarrier
                        ? 'border-status-orange-border bg-status-orange-bg text-status-orange-fg'
                        : 'border-border',
                    )}
                    title={
                      cell.usedDefaultCarrier
                        ? `${cell.country} : aucune règle, transporteur par défaut ${cell.code}`
                        : `${cell.country} : règle « ${cell.ruleName ?? ''} » → ${cell.code}`
                    }
                  >
                    {cell.usedDefaultCarrier ? (
                      <AlertTriangle className="size-3" aria-label="Transporteur par défaut" />
                    ) : null}
                    <span className="font-medium">{cell.country}</span>
                    <span className={cell.usedDefaultCarrier ? undefined : 'text-muted-foreground'}>
                      {cell.code}
                    </span>
                  </span>
                ))}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
